import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  ChevronRightIcon,
  Empty,
  Field,
  LockIcon,
  Note,
  Panel,
  ShieldIcon,
  Spinner,
} from '@wyocspay/ui';
import { CURRENCIES, currencyName } from '@wyocspay/shared/regions';
import { dateTime } from './format.js';
import {
  ApiError,
  api,
  type AccountDetailField,
  type AccountDetailRequest,
  type OnboardingState,
} from './api.js';

/**
 * Your details, and one place to ask for any of them to change.
 *
 * The merchant is not asked to work out who owns what. They see every detail on
 * one page, in their own words, and each one carries the only sentence that
 * matters: what happens if you want this changed. Three answers, decided by the
 * server in services/merchant-details.ts rather than here:
 *
 *   - ours to hold, so ask and a person will decide;
 *   - Stripe's to hold, so change it yourself in Stripe's own panel - there is
 *     deliberately no form here, because a form would mean sending identity
 *     documents and bank details to this platform;
 *   - already yours, so go and change it.
 *
 * The panel renders from `route` and never from its own idea of which field is
 * which. A field the server moves between groups moves here with it, and a
 * field it will not collect cannot be given a text box by this file.
 */

const GROUPS: Array<{
  owner: AccountDetailField['owner'];
  title: string;
  blurb: string;
}> = [
  {
    owner: 'platform',
    title: 'Details we hold for you',
    blurb:
      'These live in your WyoCSPay account. Ask for a change and somebody on the platform team reads it, decides, and writes back with the reason either way.',
  },
  {
    owner: 'stripe',
    title: 'Details Stripe holds',
    blurb:
      'Your legal business details, the people behind it, your bank account and your documents are collected and verified by Stripe. WyoCSPay never receives them, which is why these open Stripe’s own panel instead of a form. Stripe re-checks whatever you change, and can ask for a document while it does.',
  },
  {
    owner: 'merchant',
    title: 'Yours to change now',
    blurb: 'Nothing here needs approving and nothing waits on us.',
  },
];

export function AccountDetailsView({ onNavigate }: { onNavigate: (view: string, tab?: string) => void }) {
  const [fields, setFields] = useState<AccountDetailField[] | null>(null);
  const [requests, setRequests] = useState<AccountDetailRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [asking, setAsking] = useState<AccountDetailField | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const state = await api.accountDetails();
      setFields(state.fields);
      setRequests(state.requests);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <Spinner label="Loading your details…" />;
  if (error) return <Note tone="danger">{error}</Note>;
  if (!fields) return null;

  const open = requests.filter((r) => r.status === 'SUBMITTED');
  const decided = requests.filter((r) => r.status !== 'SUBMITTED');

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="Ask us about anything on this page">
        Some of what is below is ours to change, some is held and verified by Stripe, and some is
        already yours. You do not need to know which - every detail says what happens next.
      </Note>

      {flash ? <Note tone="ok">{flash}</Note> : null}

      <VerifiedDetailsPanel onNavigate={onNavigate} />

      {open.length > 0 ? (
        <Panel title="Waiting on us" padded={false}>
          {open.map((r) => (
            <RequestRow
              key={r.id}
              request={r}
              onChanged={async (message) => {
                setFlash(message);
                await load();
              }}
            />
          ))}
        </Panel>
      ) : null}

      {asking ? (
        <RequestForm
          field={asking}
          onCancel={() => setAsking(null)}
          onDone={async () => {
            setAsking(null);
            setFlash('Sent. You will see the decision here, with the reason.');
            await load();
          }}
        />
      ) : null}

      {GROUPS.map((group) => {
        const inGroup = fields.filter((f) => f.owner === group.owner);
        if (inGroup.length === 0) return null;
        return (
          <Panel key={group.owner} title={group.title}>
            <p style={{ margin: '0 0 16px', fontSize: 13.5, color: 'var(--wc-muted)', maxWidth: '68ch' }}>
              {group.blurb}
            </p>
            <div style={{ display: 'grid', gap: 12 }}>
              {inGroup.map((f) => (
                <FieldRow
                  key={f.key}
                  field={f}
                  busy={asking !== null}
                  onAsk={() => {
                    setFlash(null);
                    setAsking(f);
                  }}
                  onGo={() => onNavigate(f.destination!.view, f.destination!.tab)}
                />
              ))}
            </div>
          </Panel>
        );
      })}

      <Panel title="Past requests" padded={false}>
        {decided.length === 0 ? (
          <Empty>Nothing decided yet. Anything you ask for will be answered here, with a reason.</Empty>
        ) : (
          decided.map((r) => <RequestRow key={r.id} request={r} onChanged={load} />)
        )}
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------

function FieldRow({
  field,
  busy,
  onAsk,
  onGo,
}: {
  field: AccountDetailField;
  busy: boolean;
  onAsk: () => void;
  onGo: () => void;
}) {
  const pending = Boolean(field.pendingRequestId);

  return (
    <div
      style={{
        display: 'flex',
        gap: 16,
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: '12px 14px',
        background: 'var(--wc-surface-alt)',
        border: '1px solid var(--wc-border)',
      }}
    >
      <div style={{ flex: 1, minWidth: 240, maxWidth: '68ch' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14.5 }}>{field.label}</strong>
          {pending ? <Badge tone="pending">CHANGE REQUESTED</Badge> : null}
          {field.route === 'refer' ? (
            <Badge tone="neutral" icon={false}>
              <LockIcon size={11} /> DECIDED BY US
            </Badge>
          ) : null}
        </div>

        {field.currentValue !== null ? (
          <p
            style={{
              margin: '5px 0 0',
              fontSize: 14,
              color: 'var(--wc-ink)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {field.currentValue.length > 0 ? (
              field.currentValue
            ) : (
              <span style={{ color: 'var(--wc-faint)' }}>Not set</span>
            )}
          </p>
        ) : (
          <p style={{ margin: '5px 0 0', fontSize: 13.5, color: 'var(--wc-faint)' }}>
            {field.description}
          </p>
        )}

        <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--wc-muted)' }}>{field.note}</p>
      </div>

      <div style={{ flex: 'none' }}>
        {field.route === 'request' ? (
          <button
            type="button"
            className="wc-btn wc-btn-sm wc-btn-secondary"
            disabled={pending || busy}
            onClick={onAsk}
          >
            {pending ? 'Waiting on us' : 'Request a change'}
          </button>
        ) : field.destination ? (
          <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={onGo}>
            {field.destination.label} <ChevronRightIcon size={12} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function RequestForm({
  field,
  onCancel,
  onDone,
}: {
  field: AccountDetailField;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [value, setValue] = useState(field.currentValue ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allowEmpty = field.input?.allowEmpty === true;
  const unchanged = value.trim() === (field.currentValue ?? '').trim();
  const ready = !unchanged && (allowEmpty || value.trim().length > 0);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.requestDetailChange({
        field: field.key,
        requested_value: value,
        reason: reason.trim() || undefined,
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title={`Request a change: ${field.label}`}>
      <div style={{ marginBottom: 16 }}>
        <Note>{field.note}</Note>
      </div>

      {/*
        Read-only rather than disabled. A disabled input renders in the
        browser's grey, which drops below the contrast this panel holds itself
        to everywhere else, and it reads as "you may not do this" rather than
        "this is here so you can see what you are changing".
      */}
      <Field label="What it is now">
        <input
          className="wc-input"
          value={field.currentValue ?? ''}
          readOnly
          aria-readonly="true"
          tabIndex={-1}
          style={{ background: 'var(--wc-surface-alt)', color: 'var(--wc-muted)' }}
        />
      </Field>

      <Field
        label="What it should be"
        hint={
          allowEmpty
            ? 'Leave this empty to have the detail removed.'
            : field.input?.type === 'currency'
              ? 'Only affects payments you create after the change.'
              : undefined
        }
      >
        {field.input?.type === 'currency' ? (
          <select className="wc-input" value={value} onChange={(e) => setValue(e.target.value)}>
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.symbol} {currencyName(c.code)} ({c.code.toUpperCase()})
              </option>
            ))}
          </select>
        ) : field.key === 'contact_address' ? (
          <textarea
            className="wc-input"
            rows={3}
            value={value}
            maxLength={field.input?.maxLength}
            placeholder={field.input?.placeholder}
            onChange={(e) => setValue(e.target.value)}
            style={{ resize: 'vertical' }}
          />
        ) : (
          <input
            className="wc-input"
            type={field.input?.type === 'email' ? 'email' : 'text'}
            inputMode={field.key === 'contact_phone' ? 'tel' : undefined}
            autoComplete={
              field.input?.type === 'email' ? 'email' : field.key === 'contact_phone' ? 'tel' : 'off'
            }
            value={value}
            maxLength={field.input?.maxLength}
            placeholder={field.input?.placeholder}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
      </Field>

      <Field label="Why (optional)" hint="A line of context makes this quicker to decide.">
        <input
          className="wc-input"
          value={reason}
          maxLength={2000}
          placeholder="e.g. We incorporated last month and the name changed."
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>

      {/*
        Beside the field it belongs to rather than at the top of the form, and
        as a Note, whose danger tone carries role="alert" so it is announced
        rather than merely turning something red.
      */}
      {error ? (
        <div style={{ marginBottom: 12 }}>
          <Note tone="danger">{error}</Note>
        </div>
      ) : null}

      {unchanged && value.length > 0 ? (
        <div style={{ marginBottom: 12 }}>
          <Note tone="warn">That is what it already says. Change it to ask for something different.</Note>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="wc-btn" onClick={submit} disabled={busy || !ready}>
          {busy ? 'Sending…' : 'Send request'}
        </button>
        <button type="button" className="wc-btn wc-btn-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------

/**
 * The business details verified through the KYC gate, shown here read-only once
 * the account is verified, with the one action that belongs to them: request a
 * change. The fields themselves are entered and changed only in Verification -
 * this is the doorway back to it, because that view's own nav link is gone once
 * the account is verified.
 */
function VerifiedDetailsPanel({ onNavigate }: { onNavigate: (view: string, tab?: string) => void }) {
  const [state, setState] = useState<OnboardingState | null>(null);

  useEffect(() => {
    let live = true;
    api
      .onboarding()
      .then((s) => live && setState(s))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  if (!state || !state.verified) return null;

  const row = (label: string, value: string) => (
    <li>
      {label}: <b>{value || '-'}</b>
    </li>
  );

  return (
    <Panel title="Verified business details">
      <div style={{ display: 'grid', gap: 10 }}>
        <Note>
          These were verified with your documents. To change any of them, request a change and attach
          proof; your account stays active while we review it.
        </Note>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.8 }}>
          {row('Company registration no.', state.data.company_registration_no)}
          {row('EIN / Tax ID', state.data.tax_id)}
          {row('VAT number', state.data.vat)}
          {row('DBA / Trading name', state.data.dba)}
          {row('Representative title', state.data.representative_title)}
          {row('Representative role', state.data.representative_role)}
        </ul>
        <div>
          <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={() => onNavigate('verification')}>
            Request a change
          </button>
        </div>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------

function RequestRow({
  request,
  onChanged,
}: {
  request: AccountDetailRequest;
  onChanged: (message: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tone =
    request.status === 'APPROVED' ? 'ok'
    : request.status === 'DENIED' ? 'fail'
    : request.status === 'WITHDRAWN' ? 'neutral'
    : 'pending';

  return (
    <div style={{ padding: 16, borderBottom: '1px solid var(--wc-border)' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <strong style={{ fontSize: 14.5 }}>{request.field_label}</strong>
        <Badge tone={tone}>{request.status}</Badge>
        <span style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>{dateTime(request.created_at)}</span>
      </div>

      <dl
        style={{
          margin: '0 0 10px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 10,
        }}
      >
        <Value label="From" value={request.current_value} />
        <Value label="To" value={request.requested_value} />
      </dl>

      {request.reason ? (
        <p style={{ margin: '0 0 10px', fontSize: 13.5, color: 'var(--wc-muted)' }}>
          You said: {request.reason}
        </p>
      ) : null}

      {request.decision_note ? (
        <div
          style={{
            padding: '8px 12px',
            marginBottom: 10,
            background: request.status === 'APPROVED' ? 'var(--wc-green-tint)' : 'var(--wc-amber-tint)',
            borderLeft: `3px solid ${request.status === 'APPROVED' ? 'var(--wc-green)' : 'var(--wc-amber)'}`,
            fontSize: 13.5,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.06em',
              color: 'var(--wc-muted)',
              marginBottom: 2,
            }}
          >
            PLATFORM TEAM · {dateTime(request.decided_at)}
          </div>
          {request.decision_note}
        </div>
      ) : null}

      {/*
        The value it actually replaced, shown only when it is not the value the
        request was raised against. It means somebody changed the detail while
        this was waiting and the platform team decided over the top of it, which
        a merchant should be able to see rather than discover.
      */}
      {request.status === 'APPROVED' &&
      request.applied_value !== null &&
      request.applied_value !== request.current_value ? (
        <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--wc-orange)' }}>
          <ShieldIcon size={12} /> This replaced &ldquo;{request.applied_value}&rdquo;, which the detail had
          been changed to while your request was waiting.
        </p>
      ) : null}

      {error ? (
        <div style={{ marginBottom: 10 }}>
          <Note tone="danger">{error}</Note>
        </div>
      ) : null}

      {request.status === 'SUBMITTED' ? (
        confirming ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13.5 }}>Withdraw this request?</span>
            <button
              type="button"
              className="wc-btn wc-btn-sm wc-btn-danger"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  await api.withdrawDetailRequest(request.id);
                  onChanged('Withdrawn. You can ask again whenever you like.');
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : String(err));
                  setConfirming(false);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? 'Withdrawing…' : 'Yes, withdraw'}
            </button>
            <button
              type="button"
              className="wc-btn wc-btn-sm wc-btn-secondary"
              disabled={busy}
              onClick={() => setConfirming(false)}
            >
              Keep it
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="wc-btn wc-btn-sm wc-btn-secondary"
            onClick={() => setConfirming(true)}
          >
            Withdraw
          </button>
        )
      ) : null}
    </div>
  );
}

function Value({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="wc-label" style={{ marginBottom: 2 }}>
        {label}
      </dt>
      <dd style={{ margin: 0, fontSize: 14, color: 'var(--wc-body)', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
        {value.length > 0 ? value : <span style={{ color: 'var(--wc-faint)' }}>Not set</span>}
      </dd>
    </div>
  );
}
